import React, { useState, useEffect } from "react";
import { Dialog } from '../dialogs/dialog';
import { Button } from "primereact/button";
import { ColorPicker } from "primereact/colorpicker";
import { Slider } from "primereact/slider";
import { useThemesData } from "../../providers/theme-management-provider";
import { applyWarmth, ColorIntf, generateSurfaceScale, parseColor } from "../../general/utilities";
import { CssProperty } from "../../general/dtos";

interface EssentialsConfig {
    primaryColor: ColorIntf;
    surfaceGround: ColorIntf;
    textColor: ColorIntf;
    textColorSecondary: ColorIntf;
    borderRadius: number;
    warmth: number;
    pivotIndex: number;
    contentPadding: number;
    inlineSpacing: number;
    surfaceHover: ColorIntf;
    highlightBg: ColorIntf;
    highlightTextColor: ColorIntf;
    colorScheme: "light" | "dark";
}

export const EssentialsDialog: React.FC<{ visible: boolean; onHide: () => void }> = ({ visible, onHide }) => {
    const { findCssValue, bulkUpdateCssProperties } = useThemesData();

    const [config, setConfig] = useState<EssentialsConfig>({
        primaryColor: parseColor("#3b82f6"),
        surfaceGround: parseColor("#ffffff"),
        textColor: parseColor("#333333"),
        textColorSecondary: parseColor("#6c757d"),
        borderRadius: 4,
        warmth: 0,
        pivotIndex: 5,
        contentPadding: 1,
        inlineSpacing: 0.5,
        surfaceHover: parseColor("#f3f2f1"),
        highlightBg: parseColor("#edebe9"),
        highlightTextColor: parseColor("#323130"),
        colorScheme: "light",
    });

    useEffect(() => {
        if (visible) {
            const getSource = (key: string) => findCssValue(".essentials-theme-source", key);
            const getRoot = (key: string) => findCssValue(":root", key);
            
            // Helper to get value: Try Source, fallback to Root
            const getValue = (sourceKey: string, rootProp: string, defaultValue: string) => {
                return getSource(sourceKey) || getRoot(rootProp) || defaultValue;
            };

            const colorScheme: "dark" | "light" = getRoot("color-scheme") === "dark" ? "dark" : "light";

            setConfig({
                primaryColor: parseColor(getValue("primary-color", "--primary-color", "#3b82f6")),
                surfaceGround: parseColor(getValue("surface-ground", "--surface-ground", "#ffffff")),
                textColor: parseColor(getValue("text-color", "--text-color", "#333333")),
                textColorSecondary: parseColor(getValue("--text-color-secondary", "--text-color-secondary", "#6c757d")),
                borderRadius: parseInt(getValue("border-radius", "--border-radius", "4")),
                warmth: parseInt(getSource("warmth") || "0"), // Always default warmth to 0
                pivotIndex: parseInt(getSource("pivot-index") || "5"), // Always default pivot index to 5
                contentPadding: parseFloat(getValue("content-padding", "--content-padding", "1")),
                inlineSpacing: parseFloat(getValue("inline-spacing", "--inline-spacing", "0.5")),
                surfaceHover: parseColor(getValue("surface-hover", "--surface-hover", "#f3f2f1")),
                highlightBg: parseColor(getValue("highlight-bg", "--highlight-bg", "#edebe9")),
                highlightTextColor: parseColor(getValue("highlight-text-color", "--highlight-text-color", "#323130")),
                colorScheme: colorScheme,
            });
        }
    }, [visible, findCssValue]);

    const toHex = (c: ColorIntf) => 
        `#${((1 << 24) + (c.r << 16) + (c.g << 8) + c.b).toString(16).slice(1)}`;

    const updateColor = (key: keyof EssentialsConfig, newHex: string) => {
        const hex: string = newHex.startsWith('#') ? newHex : `#${newHex}`;
        const parsed = parseColor(hex);
        setConfig(prev => ({ 
            ...prev, 
            [key]: { ...parsed, a: (prev[key] as ColorIntf).a } 
        }));
    };

    const updateAlpha = (key: keyof EssentialsConfig, newAlpha: number) => {
        setConfig(prev => ({
            ...prev,
            [key]: { ...(prev[key] as ColorIntf), a: newAlpha / 100 }
        }));
    };

    const handleApply = (): void => {
        // 1. Calculate the warmth-transformed values
        // Using the raw base values stored in 'config'
        const warmed = {
            primary: applyWarmth(config.primaryColor, config.warmth),
            text: applyWarmth(config.textColor, config.warmth),
            textSec: applyWarmth(config.textColorSecondary, config.warmth),
            hover: applyWarmth(config.surfaceHover, config.warmth),
            hBg: applyWarmth(config.highlightBg, config.warmth),
            hText: applyWarmth(config.highlightTextColor, config.warmth),
        };

        const serialize = (c: ColorIntf) => `rgba(${c.r}, ${c.g}, ${c.b}, ${c.a})`;

        const surface: CssProperty[] = generateSurfaceScale(config.surfaceGround, config.colorScheme, config.pivotIndex);

        // 2. Persist to UI (:root)
        // We apply all 11 properties here to ensure the browser uses the latest values
        bulkUpdateCssProperties(":root", [
            { property: "--primary-color", value: serialize(warmed.primary) },
            { property: "--text-color", value: serialize(warmed.text) },
            { property: "--text-color-secondary", value: serialize(warmed.textSec) },
            { property: "--surface-hover", value: serialize(warmed.hover) },
            { property: "--highlight-bg", value: serialize(warmed.hBg) },
            { property: "--highlight-text-color", value: serialize(warmed.hText) },
            { property: "--surface-ground", value: serialize(config.surfaceGround) },
            { property: "--border-radius", value: `${config.borderRadius}px` },
            { property: "--content-padding", value: `${config.contentPadding}rem` },
            { property: "--inline-spacing", value: `${config.inlineSpacing}rem` },
            ...surface
        ], 'merge');

        // 3. Persist Raw Base Inputs to Source (.essentials-theme-source)
        // Only saving the base values and warmth level to keep the source clean
        bulkUpdateCssProperties(".essentials-theme-source", [
            { property: "primary-color", value: serialize(config.primaryColor) },
            { property: "text-color", value: serialize(config.textColor) },
            { property: "text-color-secondary", value: serialize(config.textColorSecondary) },
            { property: "surface-hover", value: serialize(config.surfaceHover) },
            { property: "highlight-bg", value: serialize(config.highlightBg) },
            { property: "highlight-text-color", value: serialize(config.highlightTextColor) },
            { property: "warmth", value: config.warmth.toString() },
            { property: "pivot-index", value: config.pivotIndex.toString() },
        ], 'replace');

        onHide();
    };

    const renderColorRow = (label: string, configKey: keyof EssentialsConfig) => {
        const color = config[configKey] as ColorIntf;
        return (
            <div className="flex align-items-center mb-2">
                {/* Main Label: Fixed width, left aligned */}
                <label style={{ width: '120px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {label}:
                </label>
                
                {/* Right Aligned Wrapper: margin-left: auto pushes everything to the right */}
                <div className="flex align-items-center" style={{ marginLeft: 'auto', gap: '8px' }}>
                    {/* Color Picker: Fixed width 64px */}
                    <div style={{ width: '64px', flexShrink: 0 }}>
                        <ColorPicker 
                            value={toHex(color)} 
                            onChange={(e) => {
                                updateColor(configKey, e.value as string);
                            }} 
                        />
                    </div>
                    <span className="text-sm font-semibold">Tarnsparency:</span>
                    <Slider 
                        value={color.a * 100} 
                        min={0} max={100} 
                        onChange={(e) => updateAlpha(configKey, e.value as number)} 
                        style={{ width: '150px' }} 
                    />
                </div>
            </div>
        );
    };

    const renderSlider = (label: string, key: keyof EssentialsConfig, min: number, max: number, step = 1) => {
        return (
            <div className="field mb-2">
                <label className="text-sm block mb-1">{label}</label>
                <Slider 
                    value={config[key] as number} 
                    min={min} max={max} step={step} 
                    onChange={(e) => setConfig({...config, [key]: e.value})} 
                />
            </div>
        );
    };

    return (
        <Dialog header="Essentials" visible={visible} onHide={onHide} style={{ width: '1000px' }}>
            <div className="grid grid-nogutter p-fluid">
                {/* Column 1: Colors 1-4 */}
                <div className="col-6 pr-3">
                    {renderColorRow("Primary", "primaryColor")}
                    {renderColorRow("Surface", "surfaceGround")}
                    {renderColorRow("Text", "textColor")}
                    {renderColorRow("Secondary text", "textColorSecondary")}

                    {renderSlider("Warmth", "warmth", 0, 100)}
                    {renderSlider("Pivot index", "pivotIndex", 0, 10)}
                    <div className="flex mt-3" style={{ height: '30px', borderRadius: '4px', overflow: 'hidden' }}>
                        {generateSurfaceScale(config.surfaceGround, config.colorScheme, config.pivotIndex).map((item, i) => (
                            <div 
                                key={i} 
                                style={{ 
                                    flex: 1, 
                                    backgroundColor: item.value, 
                                    borderLeft: '1px solid rgba(0,0,0,0.1)' 
                                }} 
                                title={item.property}
                            />
                        ))}
                    </div>
                </div>
                
                {/* Column 2: Colors 5-7 + Sliders */}
                <div className="col-6 pl-3">
                    {renderColorRow("Hover", "surfaceHover")}
                    {renderColorRow("Highlight bg", "highlightBg")}
                    {renderColorRow("Highlight text", "highlightTextColor")}
                    <div className="field mb-2"><ColorPicker style={{ visibility: "hidden" }} /></div>
                    {renderSlider("Padding", "contentPadding", 0.5, 3, 0.1)}
                    {renderSlider("Spacing", "inlineSpacing", 0.1, 2, 0.1)}
                    {renderSlider("Radius", "borderRadius", 0, 20)}
                </div>
            </div>
            <div className="admin-toolbar flex justify-content-end mt-4 gap-2">
                <Button label="Cancel" icon="pi pi-times" onClick={onHide} className="p-button-secondary" />
                <Button label="Apply" icon="pi pi-check" onClick={handleApply} className="p-button-primary" />
            </div>
        </Dialog>
    );
}
